// src/components/admin/UpdateProduct.js
import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackToDashboard from "../common/BackToAdminDashboard";

const UpdateProduct = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    external_id: "",
    name_en: "",
    name_el: "",
    subtitle_en: "",
    subtitle_el: "",
    country_origin: "",
    quantity: "",
    unit_en: "",
    unit_el: ""
  });
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchProducts = async (query) => {
    try {
      const res = await api.products.search(query);
      setProducts(res.data.data || []);
    } catch {
      setProducts([]);
    }
  };

  const handleSelect = async (product) => {
    setError(null);
    try {
      const res = await api.products.get(product.id);
      const full = res.data;
      setSelected(full);
      setForm({
        external_id:    full.external_id    || "",
        name_en:        full.name_en        || "",
        name_el:        full.name_el        || "",
        subtitle_en:    full.subtitle_en    || "",
        subtitle_el:    full.subtitle_el    || "",
        country_origin: full.country_origin,
        quantity:       full.quantity,
        unit_en:        full.unit_en        || "",
        unit_el:        full.unit_el        || ""
      });
      setProducts([]);
    } catch {
      setError(t("failed_to_fetch_product_details"));
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selected) {
      setError(t("please_select_a_product_to_update"));
      return;
    }
    setError(null);
    try {
      await api.products.update(selected.id, {
        external_id:    form.external_id,
        name_en:        form.name_en,
        name_el:        form.name_el,
        subtitle_en:    form.subtitle_en,
        subtitle_el:    form.subtitle_el,
        country_origin: parseInt(form.country_origin, 10),
        quantity:       parseInt(form.quantity, 10),
        unit_en:        form.unit_en,
        unit_el:        form.unit_el
      });
      navigate("/admin/dashboard");
    } catch {
      setError(t("failed_to_update_product"));
    }
  };

  return (
    <Container sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />
      <Typography variant="h4" gutterBottom>
        {t("update_product")}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
        <Autocomplete
          freeSolo
          options={products.map((p) => ({ id: p.id, label: `${p.name} (${p.unit})` }))}
          onInputChange={(event, value) => {
            setSearchQuery(value);
            if (value.length >= 2) fetchProducts(value);
          }}
          onChange={(event, newValue) => {
            const selectedProduct = products.find(p => p.id === newValue?.id);
            if (selectedProduct) handleSelect(selectedProduct);
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("search_product")} fullWidth />
          )}
        />
      </Box>

      {selected && (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("external_id")}
              name="external_id"
              fullWidth
              value={form.external_id}
              onChange={handleChange}
            />
            <TextField
              label={t("name_en")}
              name="name_en"
              fullWidth
              required
              value={form.name_en}
              onChange={handleChange}
            />
            <TextField
              label={t("name_el")}
              name="name_el"
              fullWidth
              required
              value={form.name_el}
              onChange={handleChange}
            />
            <TextField
              label={t("subtitle_en")}
              name="subtitle_en"
              fullWidth
              value={form.subtitle_en}
              onChange={handleChange}
            />
            <TextField
              label={t("subtitle_el")}
              name="subtitle_el"
              fullWidth
              value={form.subtitle_el}
              onChange={handleChange}
            />
            <FormControl fullWidth required>
              <InputLabel>{t("country_origin")}</InputLabel>
              <Select
                name="country_origin"
                value={form.country_origin}
                onChange={handleChange}
                label={t("country_origin")}
              >
                <MenuItem value={1}>Ελλάδα</MenuItem>
                <MenuItem value={2}>USA</MenuItem>
                <MenuItem value={3}>UK</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t("quantity")}
              name="quantity"
              type="number"
              fullWidth
              required
              value={form.quantity}
              onChange={handleChange}
            />
            <TextField
              label={t("unit_en")}
              name="unit_en"
              fullWidth
              value={form.unit_en}
              onChange={handleChange}
            />
            <TextField
              label={t("unit_el")}
              name="unit_el"
              fullWidth
              value={form.unit_el}
              onChange={handleChange}
            />
            <Button variant="contained" color="primary" type="submit">
              {t("update_product")}
            </Button>
          </Box>
        </form>
      )}
    </Container>
  );
};

export default UpdateProduct;
